/**
 * Accessibility utilities
 *
 * Functions for improving accessibility of the application.
 */

type AriaLivePriority = 'polite' | 'assertive';

interface KeyboardShortcutOptions {
  ctrl?: boolean;
  shift?: boolean;
  announce?: string;
}

/**
 * Announce a message to screen readers using aria-live region
 */
export function announce(message: string, priority: AriaLivePriority = 'polite'): void {
  const announcer = document.getElementById('sr-announcer');
  if (!announcer) {
    console.warn('Screen reader announcer element not found');
    return;
  }

  // Set the priority
  announcer.setAttribute('aria-live', priority);

  // Clear then set to trigger announcement
  announcer.textContent = '';

  // Use setTimeout to ensure screen readers detect the change
  setTimeout(() => {
    announcer.textContent = message;
  }, 50);
}

/**
 * Create a visually hidden element for screen readers
 */
export function createSrOnlyElement(text: string): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = 'sr-only';
  el.textContent = text;
  return el;
}

/**
 * Add keyboard shortcut handler with optional announcement
 */
export function addKeyboardShortcut(
  key: string,
  handler: (event: KeyboardEvent) => void,
  options: KeyboardShortcutOptions = {}
): void {
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    const ctrlPressed = e.ctrlKey || e.metaKey;
    const shiftPressed = e.shiftKey;

    if (options.ctrl && !ctrlPressed) {
      return;
    }
    if (options.shift && !shiftPressed) {
      return;
    }
    if (!options.ctrl && ctrlPressed) {
      return;
    }
    if (!options.shift && shiftPressed) {
      return;
    }

    if (e.key.toLowerCase() === key.toLowerCase()) {
      e.preventDefault();
      handler(e);
      if (options.announce) {
        announce(options.announce);
      }
    }
  });
}

/**
 * Trap focus within an element (for modals/dialogs)
 * Returns a cleanup function
 */
export function trapFocus(element: HTMLElement): () => void {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  const focusableElements = element.querySelectorAll<HTMLElement>(focusableSelectors);
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  function handleTab(e: KeyboardEvent): void {
    if (e.key !== 'Tab') {
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable?.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable?.focus();
      }
    }
  }

  element.addEventListener('keydown', handleTab);

  // Focus first element
  firstFocusable?.focus();

  // Return cleanup function
  return () => {
    element.removeEventListener('keydown', handleTab);
  };
}
